import {
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  resource,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SupportService } from '@features/support/services/support.service';
import { DatePipe } from '@angular/common';
import { ChatSendMessageComponent } from '../../../chat/chat-send-message/chat-send-message.component';
import { NgScrollbarModule } from 'ngx-scrollbar';
import { v4 as uuidv4 } from 'uuid';
import { debounceTime } from 'rxjs/operators';
import { fromEvent } from 'rxjs';
import { SvgIconComponent } from 'angular-svg-icon';
import { ToastrService } from 'ngx-toastr';
import { SupportTicketMessage } from '@features/support/interfaces/ticket.interface';
import { SessionService } from '@core/services/session.service';
import { sleep } from '@core/utils/sleep.util';
import { LoadingIconComponent } from '@core/components/loading-icon/loading-icon.component';
import { ChatSupportMessageComponent } from '@features/support/chat-support-message/chat-support-message.component';
@Component({
  selector: 'app-chat-support-ticket-modal',
  imports: [
    DatePipe,
    ChatSendMessageComponent,
    NgScrollbarModule,
    SvgIconComponent,
    LoadingIconComponent,
    ChatSupportMessageComponent,
  ],
  providers: [DatePipe],
  templateUrl: './chat-support-ticket-modal.component.html',
  styleUrl: './chat-support-ticket-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'modal-container flex flex-col',
  },
  standalone: true,
})
export class ChatSupportTicketModalComponent {
  ticketId = input.required<number>();

  private readonly destroyRef = inject(DestroyRef);
  supportService = inject(SupportService);
  toastrService = inject(ToastrService);
  datePipe = inject(DatePipe);
  sessionService = inject(SessionService);

  @ViewChild('messagesRef', { static: true }) messagesContainer!: ElementRef;

  isActiveScroll = signal(false);
  isShowRefreshAnimation = signal(false);

  ticketResource = resource({
    params: () => {
      return {
        ticketId: this.ticketId(),
      };
    },
    loader: ({ params }) => {
      const response = this.supportService.getTicket(params.ticketId);
      return response;
    },
  });

  category = computed<string>(() => {
    const category = this.ticketResource.value()?.ticket?.category;
    if (!category) {
      return 'Unknown Category';
    }
    return (
      this.supportService.categories().find((c) => c.id === category)?.name ??
      `Unknown Category (id:${category})`
    );
  });

  closedByUser = computed(() => {
    const isClosed = this.ticketResource.value()?.ticket.status === 'Closed';
    const isClosedByUser =
      this.ticketResource.value()?.ticket.response_userid ===
      this.sessionService.profile()?.steamid;

    return isClosed && isClosedByUser;
  });

  messages = computed<SupportTicketMessage[]>(() => {
    const ticket = this.ticketResource.value()?.ticket;
    const responses = this.ticketResource.value()?.responses;

    if (!ticket || !responses) {
      return [];
    }

    const messages: SupportTicketMessage[] = [
      ...(responses?.map((response) => {
        return {
          __id: String(uuidv4()),
          response: response.response,
          time: response.time ?? '',
          avatar: response.avatar,
          name: response.name,
          steamid: response.steamid,
          level: response.level ?? 0,
          role: response.role ?? 0,
          __outlined: true,
        };
      }) ?? []),
    ];

    const description: SupportTicketMessage | null = ticket.description
      ? {
          __id: String(uuidv4()),
          response: `${ticket.description}`,
          time: ticket.opened,
          avatar: ticket.avatar,
          name: ticket.name,
          steamid: ticket.steamid,
          level: ticket.level ?? 0,
          role: 0,
          __outlined: true,
        }
      : null;

    const topMessages = [];

    if (description) {
      topMessages.push(description);
    }

    return [...topMessages, ...messages];
  });

  goDownEffect = afterRenderEffect(() => {
    const messages = this.messages();
    if (messages.length > 0 && !this.isActiveScroll()) {
      this.goDown();
    }
  });

  ngAfterViewInit() {
    fromEvent(this.messagesContainer.nativeElement, 'scroll')
      .pipe(debounceTime(150), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const element = this.messagesContainer?.nativeElement;
        this.isActiveScroll.set(
          element.scrollTop !== element.scrollHeight - element.clientHeight
        );
      });
  }

  formatDate(date?: string | Date | number | null): string {
    if (!date || date === null) return 'Unreadable Date';
    return (
      this.datePipe.transform(date, 'MMM d, yyyy, h:mm:ss a') ??
      'Unreadable Date'
    );
  }

  goDown() {
    this.messagesContainer.nativeElement.scrollTo({
      top: this.messagesContainer.nativeElement.scrollHeight,
      behavior: 'smooth',
    });
  }

  async respondToTicket(message: string) {
    await this.supportService.respondToTicket(message, this.ticketId());
    const prev = this.ticketResource.value();
    const profile = this.sessionService.profile();

    if (prev && profile) {
      const newMessage: SupportTicketMessage = {
        time: String(new Date().getTime()),
        response: message,
        role: this.sessionService.is_admin(),
        ...profile,
        __id: String(uuidv4()),
        __outlined: true,
      };

      this.ticketResource.set({
        ...prev,
        responses: [...prev.responses, newMessage],
      });
    }

    this.toastrService.success('Ticket responded successfully');
  }

  async closeTicket() {
    await this.supportService.closeTicket(this.ticketId());

    this.ticketResource.update((prev) => {
      if (!prev) return prev;

      return {
        ...prev,
        ticket: {
          ...prev.ticket,
          response_userid: this.sessionService.profile()?.steamid ?? '',
          status: 'Closed',
        },
      };
    });

    this.supportService.ticketsResource.update((prev) =>
      prev?.map((ticket) => {
        if (ticket.id === this.ticketId()) {
          return {
            ...ticket,
            status: 'Closed',
          };
        }
        return ticket;
      })
    );
    this.toastrService.success('Ticket closed successfully');
  }

  async reOpenTicket() {
    await this.supportService.reOpenTicket(this.ticketId());
    this.toastrService.success('Ticket reopened successfully');
    this.ticketResource.reload();
  }

  async refreshTicket() {
    this.isShowRefreshAnimation.set(true);
    this.ticketResource.reload();
    await sleep(500);
    this.isShowRefreshAnimation.set(false);
  }
}
